import React from 'react';
import StyledProfileDropDown from '../../components/styledComponents/StyledProfileDropDown';
import { useLocation, useNavigate } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';
import useAuth from '../../../hooks/useAuth';
import LogoutIcon from '../../components/svgIcons/LogoutIcon';
import NotificationDropDown from '../../components/styledComponents/NotificationDropDown';
import { useGetNotificationsQuery } from '../../../features/Setting/Notifications/Apis/notificationApiSlice';
import MailBoxIcon from '../../components/svgIcons/MailBoxIcon';
import { Wrap } from '../../../assets/styledComponents/Global';
import { useGetUnreadQuery } from '../../../features/mailbox/mailBoxApi';
import StyledDot from '../../components/styledComponents/StyledDot';
import { AllowedFunc } from '../../../midleware/midleware';
import {
  PERMISSIONS,
  usePermissionActionGuard,
} from '../../../permission-system';
import ChildCompaniesDropdown from './ChildCompaniesDropdown';
import WizardIcon from '../../components/svgIcons/WizardSvg/WizardIcon';
import HeaderRing from '../../components/svgIcons/HeaderRing';

const Header = () => {
  const { user } = useAuth();
  const { runIfAllowed } = usePermissionActionGuard();
  const theme = useTheme();
  const { data: notifications, refetch } = useGetNotificationsQuery();
  const { data } = useGetUnreadQuery(null, { skip: AllowedFunc('mailbox', user) });
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isMailActive = pathname.includes('/mails');
  const isWizardActive = pathname.includes('/wizard');
  const isNotificationActive =
    pathname === '/notifications' || pathname.startsWith('/notifications/');
  const userMenuItems = [
    {
      name: 'Logout',
      icon: <LogoutIcon />,
      subItems: [{
        name: 'Logout',
        icon: <LogoutIcon />,
      }],
      action: () => {
        window.location.href = '/logout';
      },
    },
  ];

  return (
    <StyledHeader>
      <div className="d-flex justify-content-between align-items-center">
        <HeaderDecor className="d-none d-lg-block" aria-hidden="true">
          <HeaderRingCluster>
            <HeaderRingSlot className="ring-cream">
              <HeaderRing size={63.877} color="#f3dfc1" />
            </HeaderRingSlot>
            <HeaderRingSlot className="ring-teal">
              <HeaderRing size={47.289} color="#48a9a6" />
            </HeaderRingSlot>
            <HeaderRingSlot className="ring-orange">
              <HeaderRing size={32.617} color="#e96618" />
            </HeaderRingSlot>
          </HeaderRingCluster>
        </HeaderDecor>

        <div className="d-flex px-3 py-4 justify-content-between align-items-center w-100 position-relative" style={{ zIndex: 1 }}>
          <div
            className="d-flex align-items-center gap-3 flex-grow-1"
            style={{ minWidth: 0 }}
          >
            <ChildCompaniesDropdown profile={user}></ChildCompaniesDropdown>
          </div>

          <div className="d-flex align-items-center header-actions">
            <div className="header-icon-cluster">
              {!AllowedFunc('viewWizard', user) && (
                <IconButton
                  onClick={() => navigate('wizard')}
                  $active={isWizardActive}
                  className={`edit-icon-wrapper ${
                    user?.accountType === 2 && user?.company?.isParent === 1
                      ? 'd-none'
                      : ''
                  } cursor-pointer position-relative`}
                >
                  <WizardIcon size={20} color="currentColor" />
                </IconButton>
              )}

              <IconButton
                onClick={() =>
                  runIfAllowed(PERMISSIONS.ACCESS_MAILBOX, () => navigate('mails'))
                }
                className="cursor-pointer position-relative edit-icon-wrapper"
                $active={isMailActive}
                hidden={AllowedFunc('mailbox', user)}
              >
                <MailBoxIcon size={20} color="currentColor" />
                {data?.count === 0 ? null : (
                  <StyledDot
                    color={theme?.dark ? theme?.darkDoubleRed : theme.danger}
                    $position="absolute"
                    $top="-1px"
                    $right="-1px"
                    $border={`2px solid ${theme.white}`}
                    $size="8px"
                  />
                )}
              </IconButton>

              <Wrap
                className="d-flex align-items-center justify-content-center position-relative notification-slot edit-icon-wrapper"
                data-active={isNotificationActive ? 'true' : undefined}
              >
                <NotificationDropDown
                  notifications={notifications?.notifications}
                  totalUnreadNotifications={notifications?.totalUnreadNotifications}
                  menuItems={userMenuItems}
                  profile={user}
                  toggleClassName={'bg-transparent p-0'}
                  menuClassName="rounded-4 Larger shadow "
                  refetch={refetch}
                />
              </Wrap>
            </div>

            <ProfileAvatarSlot>
              <StyledProfileDropDown
                menuItems={userMenuItems}
                menuClassName={`ooo`}
                profile={user}
                toggleClassName={'bg-transparent p-0'}
              />
            </ProfileAvatarSlot>
          </div>
        </div>
      </div>
    </StyledHeader>
  );
};
const StyledHeader = styled.div`
  position: sticky;
  top: 0;
  z-index: 99;
  margin-bottom: 0;
  min-height: var(--app-header-height, 72px);
  /* Must stay overflow:visible so company/profile dropdowns can open below the bar */
  overflow: visible;
  --app-header-height: 72px;
  color: ${({ theme }) => theme?.headerForeground || '#fff'};
  background-color: ${({ theme }) => theme?.headerBg || theme?.sideBar};
  box-shadow: ${({ theme }) => theme?.shadowSm || '0 1px 2px rgba(13,35,73,0.06)'};
  border-bottom: 1px solid
    ${({ theme }) => (theme?.dark ? theme?.borderSubtle : 'rgba(255,255,255,0.08)')};

  > div {
    min-height: var(--app-header-height, 72px);
    position: relative;
    overflow: visible;
  }

  /* Icon trio sits tight and even; avatar is spaced off to the right */
  .header-actions {
    display: flex;
    gap: 24px;
    flex-shrink: 0;
    align-items: center;
  }

  /* Neutralize global .menu-toggle { margin-left: -20px } so the avatar
     is not pulled into the bell. */
  .header-actions .custom-drop-down .menu-toggle,
  .header-actions .dropdown-toggle {
    margin-left: 0 !important;
    margin-right: 0 !important;
  }

  .header-icon-cluster {
    display: flex;
    align-items: center;
    gap: 20px;
  }

  .header-icon-cluster > .edit-icon-wrapper {
    width: auto;
    min-width: 22px;
    height: 22px;
    padding: 0;
    overflow: visible;
  }

  .header-icon-cluster > .edit-icon-wrapper > svg,
  .notification-slot .account-img svg {
    width: auto;
    height: 18px;
    display: block;
    flex-shrink: 0;
    overflow: visible;
  }

  .notification-slot {
    width: auto;
    min-width: 22px;
    height: 22px;
    justify-content: center;
    border-radius: ${({ theme }) => theme?.radiusSm || '8px'};
    overflow: visible;
    transition: background-color 0.15s ease;
  }

  .notification-slot .dropdown,
  .notification-slot .dropdown-toggle,
  .notification-slot .account-menu {
    display: flex;
    align-items: center;
    justify-content: center;
    width: auto;
    height: 22px;
    padding: 0 !important;
    margin: 0;
    background: transparent !important;
    border: none !important;
    box-shadow: none !important;
  }

  .notification-slot .account-img {
    width: auto !important;
    height: 18px !important;
    min-width: 0 !important;
    margin: 0 !important;
    padding: 0 !important;
    overflow: visible !important;
    background: transparent !important;
    border: none !important;
    border-radius: 0 !important;
    display: flex !important;
    align-items: center;
    justify-content: center;
  }

  .notification-slot:hover {
    background-color: transparent;
  }

  .notification-slot .dropdown-toggle::after {
    display: none;
  }

  .header-actions .account-img {
    margin-right: 0;
  }

  /* The avatar itself is the profile-menu trigger, so no caret */
  .header-actions .dropdown-toggle::after {
    display: none;
  }

  /* Do NOT force white on all .dropdown > a — nested white menus inherit that and
     become white-on-white. Trigger colour lives on the company pill itself. */
  .edit-icon-wrapper {
    color: #fff !important;
  }

  .edit-icon-wrapper svg,
  .header-actions > .edit-icon-wrapper svg {
    color: #fff;
  }

  .header-icon-cluster .edit-icon-wrapper svg path,
  .header-icon-cluster .edit-icon-wrapper svg circle,
  .header-icon-cluster .edit-icon-wrapper svg line,
  .header-icon-cluster .edit-icon-wrapper svg polyline,
  .header-icon-cluster .edit-icon-wrapper svg polygon {
    stroke: #fff !important;
    transition: stroke 0.15s ease;
  }

  .header-icon-cluster .edit-icon-wrapper:hover svg path,
  .header-icon-cluster .edit-icon-wrapper:hover svg circle,
  .header-icon-cluster .edit-icon-wrapper:hover svg line,
  .header-icon-cluster .edit-icon-wrapper:hover svg polyline,
  .header-icon-cluster .edit-icon-wrapper:hover svg polygon,
  .header-icon-cluster .edit-icon-wrapper[data-active='true'] svg path,
  .header-icon-cluster .edit-icon-wrapper[data-active='true'] svg circle,
  .header-icon-cluster .edit-icon-wrapper[data-active='true'] svg line,
  .header-icon-cluster .edit-icon-wrapper[data-active='true'] svg polyline,
  .header-icon-cluster .edit-icon-wrapper[data-active='true'] svg polygon {
    stroke: ${({ theme }) => theme.secondary || '#e96618'} !important;
  }

  .header-icon-cluster .edit-icon-wrapper svg path[fill]:not([fill='none']),
  .header-icon-cluster .edit-icon-wrapper svg circle[fill]:not([fill='none']) {
    fill: #fff !important;
  }

  .header-icon-cluster .edit-icon-wrapper:hover svg path[fill]:not([fill='none']),
  .header-icon-cluster .edit-icon-wrapper:hover svg circle[fill]:not([fill='none']),
  .header-icon-cluster .edit-icon-wrapper[data-active='true'] svg path[fill]:not([fill='none']),
  .header-icon-cluster .edit-icon-wrapper[data-active='true'] svg circle[fill]:not([fill='none']) {
    fill: ${({ theme }) => theme.secondary || '#e96618'} !important;
  }

  .dropdown-toggle::after {
    filter: brightness(0) invert(1);
    opacity: 0.8;
  }

  .section-container {
    position: sticky;
    top: 0;
  }
`;

/* Isolates the profile avatar so utility-icon SVG size rules never shrink it,
   and gives it a clear bordered tile against the navy header. */
const ProfileAvatarSlot = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  margin-left: 8px;

  .dropdown,
  .dropdown-toggle,
  .menu-toggle,
  .account-menu {
    display: flex;
    align-items: center;
    padding: 0 !important;
    margin: 0 !important;
    background: transparent !important;
    border: none !important;
    box-shadow: none !important;
  }

  .account-img {
    width: 36px !important;
    height: 36px !important;
    min-width: 36px !important;
    margin: 0 !important;
    overflow: hidden;
    border-radius: ${({ theme }) => theme?.radiusSm || '8px'} !important;
    border: 2px solid rgba(255, 255, 255, 0.85);
    box-shadow: 0 0 0 1px rgba(13, 35, 73, 0.2);
    background-color: ${({ theme }) => theme?.headerAvatarBg || '#2FB3A0'};
    color: #fff;
    display: flex !important;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    font-weight: ${({ theme }) => theme?.fontWeightSemibold || 600};
    line-height: 1;
    letter-spacing: 0.02em;
  }

  .account-img img {
    width: 100% !important;
    height: 100% !important;
    object-fit: cover;
    border-radius: ${({ theme }) => theme?.radiusSm || '8px'} !important;
    display: block;
  }

  .account-img svg {
    width: 100% !important;
    height: 100% !important;
    max-width: none !important;
    max-height: none !important;
  }

  .account-img .avatar-initials {
    color: #fff;
    user-select: none;
  }
`;

/* Cluster sits at 60% of the bar — same visual slot as the inspect
   tweak. Ring offsets stay in px so the three ellipses keep their
   overlap; the whole group scales on narrower headers instead of
   each ring getting a different left/top. */
const HeaderDecor = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 60%;
  right: 168px;
  overflow: hidden;
  pointer-events: none;
  z-index: 0;
`;

const HeaderRingCluster = styled.div`
  position: relative;
  width: 180px;
  height: 100%;
  transform-origin: left center;

  @media (max-width: 1400px) {
    transform: scale(0.92);
  }

  @media (max-width: 1200px) {
    transform: scale(0.84);
  }

  @media (max-width: 992px) {
    transform: scale(0.76);
  }
`;

const HeaderRingSlot = styled.div`
  position: absolute;

  svg {
    display: block;
  }

  &.ring-cream {
    top: -42px;
    left: -2px;
    z-index: 1;
  }

  &.ring-teal {
    top: -20px;
    left: 126px;
    z-index: 2;
  }

  &.ring-orange {
    top: 25px;
    left: 72px;
    z-index: 3;
  }
`;

const IconButton = styled.div.attrs(({ $active }) => ({
  'data-active': $active ? 'true' : undefined,
}))`
  width: auto;
  min-width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${({ theme }) => theme?.radiusSm || '8px'};
  color: ${({ $active, theme }) =>
    $active ? theme.secondary || '#e96618' : '#fff'};
  overflow: visible;
  transition: background-color 0.15s ease, color 0.15s ease;

  svg path,
  svg circle,
  svg line,
  svg polyline,
  svg polygon {
    stroke: ${({ $active, theme }) =>
      $active ? theme.secondary || '#e96618' : '#fff'} !important;
    transition: stroke 0.15s ease;
  }

  svg path[fill]:not([fill='none']) {
    fill: ${({ $active, theme }) =>
      $active ? theme.secondary || '#e96618' : '#fff'} !important;
  }

  &:hover {
    background-color: transparent;
    color: ${({ theme }) => theme.secondary || '#e96618'};
  }

  &:hover svg path,
  &:hover svg circle,
  &:hover svg line,
  &:hover svg polyline,
  &:hover svg polygon {
    stroke: ${({ theme }) => theme.secondary || '#e96618'} !important;
  }

  &:hover svg path[fill]:not([fill='none']) {
    fill: ${({ theme }) => theme.secondary || '#e96618'} !important;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.secondary || '#dc601e'};
    outline-offset: 2px;
  }
`;

export default Header;
